import { Button, Popover, PopoverContent, PopoverTrigger } from '@ibsys/kit';
import { LocateIcon, PlayIcon, SquareStopIcon } from 'lucide-react';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib';
import { useDialogSessionOptional } from '../../model/useDialogSession';
import type { DialogSessionAction } from './types';

/**
 * Пропсы компонента {@link DialogSessionActions}.
 */
type Props = {
  /** Временная метка — строка (например, «08:00» или «1:23:45») или число секунд */
  time: string | number;
  /** Список действий, отображаемых в поповере */
  actions: DialogSessionAction[];
  /** Действие, выполняемое автоматически при открытии поповера */
  clickAction?: 'scrollTo' | 'play';
  /** Если true, children должен быть единственным React-элементом и использоваться как триггер поповера напрямую */
  asChild?: boolean;
  /** Содержимое триггера */
  children: React.ReactNode;
  /** CSS-класс для триггера */
  className?: string;
  /** Если true, при воспроизведении плеер автоматически прокручивается к текущей позиции */
  scrollByPlay?: boolean;
};

const triggerClassName =
  'inline text-primary cursor-pointer hover:text-primary/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 rounded-sm';

/**
 * Обёртка над временной меткой диалога, открывающая popover с набором действий над сессией.
 *
 * При наличии `clickAction` — соответствующее действие выполняется автоматически
 * в момент открытия поповера (например, сразу запускает воспроизведение при клике).
 * Если контекст {@link useDialogSessionOptional} недоступен, все кнопки действий отключены.
 */
export const DialogSessionActions: FC<Props> = ({
  time,
  actions,
  clickAction,
  asChild,
  children,
  className,
  scrollByPlay,
}) => {
  const { t } = useTranslation('dialogs');
  const dialogSession = useDialogSessionOptional();
  const [isOpen, setIsOpen] = useState(false);

  const handleScrollTo = useCallback(() => {
    dialogSession?.scrollToTime(time);
  }, [dialogSession?.scrollToTime, time]);

  const handlePlay = useCallback(() => {
    dialogSession?.playAtTime(time, { scrollTo: scrollByPlay });
  }, [dialogSession?.playAtTime, time, scrollByPlay]);

  const handleStop = useCallback(() => {
    dialogSession?.pause();
  }, [dialogSession?.pause]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      if (open) {
        if (clickAction === 'play') {
          handlePlay();
        } else if (clickAction === 'scrollTo') {
          dialogSession?.scrollToTime(time);
        }
      }
    },
    [clickAction, handlePlay, dialogSession?.scrollToTime, time],
  );

  const isPlaying = dialogSession?.isPlaying ?? false;

  const actionButtons = useMemo(() => {
    const map: Record<DialogSessionAction, React.ReactNode> = {
      play: (
        <Button
          key="play"
          variant="secondary"
          size="sm"
          icon={<PlayIcon className="h-4 w-4" />}
          onClick={handlePlay}
          disabled={!dialogSession}
        >
          {t('timeFragment.play')}
        </Button>
      ),
      stop: isPlaying && (
        <Button
          key="stop"
          variant="secondary"
          size="sm"
          icon={<SquareStopIcon className="h-4 w-4" />}
          onClick={handleStop}
          disabled={!dialogSession || !isPlaying}
        >
          {t('timeFragment.stop')}
        </Button>
      ),
      scrollTo: (
        <Button
          key="scrollTo"
          variant="secondary"
          size="sm"
          icon={<LocateIcon className="h-4 w-4" />}
          onClick={handleScrollTo}
          disabled={!dialogSession}
        >
          {t('timeFragment.scrollToTime')}
        </Button>
      ),
    };

    return actions.map((action) => map[action]);
  }, [actions, handleScrollTo, handlePlay, handleStop, dialogSession, isPlaying, t]);

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        {asChild ? (
          children
        ) : (
          <button type="button" className={cn(triggerClassName, className)}>
            {children}
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-auto p-2" align="start">
        <div className="flex flex-col gap-2">{actionButtons}</div>
      </PopoverContent>
    </Popover>
  );
};
